import { useBoolean } from 'minimal-shared/hooks'

import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Drawer from '@mui/material/Drawer'
import { Stack } from '@mui/material'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import { faClose } from '@fortawesome/pro-duotone-svg-icons'
import { NavLink } from 'react-router-dom'

import { Scrollbar } from 'src/components/shared/scrollbar'

import { AccountButton } from './account-button'
import FaIcon from '../../components/shared/faIcon/FaIcon'
import Button from '../../components/shared/button/Button'
import { RouterLink } from '../../routes/components'
import { NAVIGATION_BLANK_PARAMS } from '../../util/navigation.helpers'
import Text from '../../components/shared/text/Text'
import { Label } from '../../components/shared/label'
import ProviderIcon from '../../components/shared/exchangeProviderCard/ProviderIcon'
import UserAvatar from './user-avatar'
import { titleCase } from '../../util/string.helpers'
import AccountNumberContent from '../../components/userAccount/AccountNumberContent'
import DiscordConnect from '../../components/userAccount/DiscordConnect'
import { MEMBERSHIP_URL } from '../../constants/navigation'

export function AccountDrawer({
  data = [],
  sx,
  t,
  userInitials,
  firstName,
  lastName,
  email,
  userTier: {
    tier: { id, name }
  },
  signOut,
  authId,
  notificationBarHeight,
  exchangeMemberships,
  ...other
}) {
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean()

  const handleSignOut = () => {
    signOut()
    onClose()
  }

  const renderList = () => (
    <MenuList
      disablePadding
      sx={[
        (theme) => ({
          py: 3,
          px: 2.5,
          borderTop: `dashed 1px ${theme.vars.palette.divider}`,
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
          '& li': { p: 0 }
        })
      ]}
    >
      {data.map(({ path, icon, title, external }) => {
        const linkProps = external
          ? { href: path, ...NAVIGATION_BLANK_PARAMS }
          : { component: RouterLink, to: path }

        return (
          <MenuItem key={title}>
            <Link
              {...linkProps}
              color='inherit'
              underline='none'
              onClick={onClose}
              sx={{
                p: 1,
                width: 1,
                display: 'flex',
                typography: 'body2',
                alignItems: 'center',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              <FaIcon icon={icon} />
              <Box component='span' sx={{ ml: 1.4 }}>
                {title}
              </Box>
            </Link>
          </MenuItem>
        )
      })}
    </MenuList>
  )
  return (
    <>
      <AccountButton
        onClick={onOpen}
        userInitials={userInitials}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={onClose}
        anchor='right'
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{
          sx: {
            width: 320,
            pt: `${notificationBarHeight}px`
          }
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            top: 12 + notificationBarHeight,
            left: 12,
            zIndex: 9,
            position: 'absolute',
            width: 36,
            height: 36
          }}
        >
          <FaIcon icon={faClose} size='sm' />
        </IconButton>

        <Scrollbar>
          <Box
            sx={{
              pt: 8,
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column'
            }}
          >
            <UserAvatar
              sx={{ mb: 2, p: '6px' }}
              label={userInitials}
              fontSize={32}
              size={96}
              borderSize={120}
            />
            <Text
              variant='subtitle1'
              color=''
              sx={{ mt: 2 }}
              label={titleCase(`${firstName} ${lastName}`)}
              textAlign='center'
            />
            <Text
              variant='body2'
              sx={{ color: 'text.secondary', mt: 0.5, mb: 2 }}
              label={email}
              textAlign='center'
            />
          </Box>
          <NavLink
            to={MEMBERSHIP_URL}
            style={{ textDecoration: 'none' }}
            onClick={onClose}
          >
            <Stack
              spacing={2}
              alignItems='center'
              justifyContent='center'
              direction='row'
              sx={{ mb: 3 }}
            >
              <ProviderIcon size={56} src={`/assets/tiers/${id}-s.png`} />
              <Label color='primary'>
                {t(
                  `brand:staking.membership-${id === 1 ? 'starter' : 'level'}-title`,
                  {
                    number: id - 1,
                    name
                  }
                )}
              </Label>
            </Stack>
          </NavLink>
          <Stack
            sx={[
              (theme) => ({
                py: 3,
                textAlign: 'center',
                borderTop: `dashed 1px ${theme.vars.palette.divider}`
              })
            ]}
          >
            <Text
              variant='subtitle1'
              color=''
              label={t('exchange.account-number')}
              textAlign='center'
              sx={{ lineHeight: 1, mb: 1.5 }}
            />
            <AccountNumberContent authId={authId} t={t} iconAction />
          </Stack>
          <Stack
            sx={[
              (theme) => ({
                py: 3,
                textAlign: 'center',
                borderTop: `dashed 1px ${theme.vars.palette.divider}`
              })
            ]}
          >
            <DiscordConnect t={t} exchangeMemberships={exchangeMemberships} />
          </Stack>
          {renderList()}
        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <Button
            fullWidth
            variant='soft'
            size='large'
            color='error'
            onClick={handleSignOut}
            label={t('nav-header.log-out')}
          />
        </Box>
      </Drawer>
    </>
  )
}
